import { CommonModule } from '@angular/common';
import { Component, OnInit, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastService } from 'angular-toastify';
import {
  ParkingWebStampHistoryRow,
  ParkingWebStampSearchField,
} from '../../../../../core/models/parking/parking-web-stamp-history.model';
import { MapperParkingWebStampHistoryService } from '../../../../../core/mappers/parking/mapper-parking-web-stamp-history.service';
import { ApiStampDiscountService } from '../../../../../core/services/api/parking/api-stamp-discount.service';
import { SiteStateService } from '../../../../../core/services/site-state.service';
import { PssLoadingComponent } from '../../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ModalWebStampHistoryDetailComponent } from '../../../modals/parking/modal-web-stamp-history-detail/modal-web-stamp-history-detail.component';

@Component({
  selector: 'app-web-stamp-history',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalWebStampHistoryDetailComponent],
  templateUrl: './web-stamp-history.component.html',
  styleUrl: './web-stamp-history.component.css',
})
export class WebStampHistoryComponent implements OnInit {
  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiStampDiscountService);
  private readonly mapper = inject(MapperParkingWebStampHistoryService);
  private readonly toast = inject(ToastService);

  readonly pageSizeOptions = [20, 50, 100] as const;

  items = signal<ParkingWebStampHistoryRow[]>([]);
  isLoading = signal(false);
  currentPage = signal(1);
  pageSize = signal(20);
  totalItems = signal(0);
  totalPages = signal(1);
  lastFetchedTime: Date | null = null;

  showImageModal = false;
  selectedImageUrl = '';
  showDetailModal = signal(false);
  selectedDetail = signal<ParkingWebStampHistoryRow | null>(null);
  isPageSizeDropdownOpen = false;

  /** yyyy-MM-dd — same pattern as transaction-parking */
  startDate = '';
  endDate = '';

  searchField: ParkingWebStampSearchField = 'userDisplayName';
  searchText = '';
  isSearchTypeDropdownOpen = false;

  readonly searchTypeOptions: { value: ParkingWebStampSearchField; label: string; icon: string }[] = [
    { value: 'userDisplayName', label: 'ชื่อผู้ให้ส่วนลด', icon: 'person' },
    { value: 'license', label: 'ทะเบียนรถ', icon: 'directions_car' },
    { value: 'rateName', label: 'ชื่อส่วนลด', icon: 'local_offer' },
  ];

  constructor() {
    effect(() => {
      const siteId = this.siteState.selectedSite()?.id;
      untracked(() => {
        this.currentPage.set(1);
        if (siteId) this.loadTable();
        else this.items.set([]);
      });
    });
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get currentSearchTypeLabel(): string {
    return this.searchTypeOptions.find((o) => o.value === this.searchField)?.label ?? 'ค้นหา';
  }

  get startItemIndex(): number {
    if (this.totalItems() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage() * this.pageSize(), this.totalItems());
  }

  get pagesArray(): number[] {
    const total = this.totalPages();
    const maxVisible = 5;
    let start = Math.max(1, this.currentPage() - 2);
    let end = Math.min(total, start + maxVisible - 1);
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  ngOnInit(): void {
    const today = new Date().toISOString().split('T')[0];
    this.startDate = today;
    this.endDate = today;
    if (this.selectedSiteId) this.loadTable();
  }

  toggleSearchTypeDropdown(): void {
    this.isSearchTypeDropdownOpen = !this.isSearchTypeDropdownOpen;
  }

  selectSearchType(value: ParkingWebStampSearchField): void {
    this.searchField = value;
    this.isSearchTypeDropdownOpen = false;
    if (this.searchText.trim()) {
      this.currentPage.set(1);
      this.loadTable();
    }
  }

  onSearchSubmit(): void {
    this.currentPage.set(1);
    this.loadTable();
  }

  onDateChange(): void {
    this.currentPage.set(1);
    this.loadTable();
  }

  onPageSizeChange(size: number): void {
    this.pageSize.set(size);
    this.currentPage.set(1);
    this.loadTable();
  }

  onRefresh(): void {
    this.currentPage.set(1);
    this.loadTable();
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
      this.loadTable();
    }
  }

  trackById(_index: number, item: ParkingWebStampHistoryRow): number {
    return item.id;
  }

  formatStampTime(value: string | null): string {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${mi}:${ss}`;
  }

  openImageModal(url: string | null | undefined): void {
    if (!url) return;
    this.selectedImageUrl = url;
    this.showImageModal = true;
  }

  closeImageModal(): void {
    this.showImageModal = false;
    this.selectedImageUrl = '';
  }

  openDetail(item: ParkingWebStampHistoryRow): void {
    this.selectedDetail.set(item);
    this.showDetailModal.set(true);
  }

  closeDetail(): void {
    this.showDetailModal.set(false);
    this.selectedDetail.set(null);
  }

  remarkPreview(remark: string | null | undefined): string {
    const text = (remark || '').trim();
    if (!text) return '—';
    return text.length > 48 ? `${text.slice(0, 48)}…` : text;
  }

  hasLongRemark(remark: string | null | undefined): boolean {
    return (remark || '').trim().length > 48;
  }

  togglePageSizeDropdown(): void {
    this.isPageSizeDropdownOpen = !this.isPageSizeDropdownOpen;
  }

  closePageSizeDropdown(): void {
    this.isPageSizeDropdownOpen = false;
  }

  selectPageSize(size: number): void {
    this.isPageSizeDropdownOpen = false;
    this.onPageSizeChange(size);
  }

  loadTable(): void {
    const siteId = this.selectedSiteId;
    const start = this.startDate?.trim() || '';
    const end = this.endDate?.trim() || '';

    if (!siteId) {
      this.items.set([]);
      return;
    }

    if (!start || !end) {
      this.toast.error('กรุณาเลือกวันที่เริ่มและสิ้นสุด');
      return;
    }

    if (start > end) {
      this.toast.error('วันเริ่มต้นต้องไม่เกินวันสิ้นสุด');
      return;
    }

    this.isLoading.set(true);
    this.api
      .getParkingWebStampForTable({
        siteId,
        startDate: start,
        endDate: end,
        page: this.currentPage(),
        pageSize: this.pageSize(),
        searchField: this.searchText.trim() ? this.searchField : undefined,
        searchText: this.searchText.trim() || undefined,
      })
      .subscribe({
        next: (res) => {
          const mapped = this.mapper.mapTable(res);
          this.items.set(mapped.data);
          this.totalItems.set(mapped.total);
          this.totalPages.set(Math.max(1, mapped.totalPages || Math.ceil(mapped.total / this.pageSize())));
          this.lastFetchedTime = new Date();
          this.isLoading.set(false);
        },
        error: () => {
          this.items.set([]);
          this.isLoading.set(false);
          this.toast.error('โหลดประวัติ stamp ล้มเหลว');
        },
      });
  }
}
